import { COLORS } from '@repo/design-system/index'
import { QIcon_chevron_bold_right } from '@repo/icons/index'
import { useEffect } from 'react'
import { GTM_CLICK_EVENT, useEventTracking } from '../../../../helpers/gtmHelper'
import { APP_SCREEN_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsInApp from '../../../../modules/hooks/common/useIsInApp'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_common, n_pricing } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { productTypeSelectorId } from '../constants'
import { BenefitBadge } from './PackageOptionCard'
import { useProductContext } from './ProductContext'
import ProductTypeCard from './ProductTypeCard'

export enum productTypeEnum {
  RINGLE_PACKAGE,
  PERSONAL_LESSON,
  AI_TUTOR,
  BUSINESS,
}

const productTypeData = [
  {
    // id: PRODUCT_TYPE.COMBINED,
    id: 24,
    title: n_pricing.package.ringle,
    subTitle: n_pricing.package.ringle_sub,
    type: productTypeEnum.RINGLE_PACKAGE,
    isRecommended: true,
    showInB2B: false,
    query: 'package',
    eventName: GTM_CLICK_EVENT.PURCHASE_PACKAGE_RINGLE,
  },
  {
    // id: PRODUCT_TYPE.NORMAL,
    id: 19,
    title: n_pricing.package.lesson,
    subTitle: n_pricing.package.lesson_sub,
    type: productTypeEnum.PERSONAL_LESSON,
    isRecommended: false,
    showInB2B: true,
    query: 'lesson',
    eventName: GTM_CLICK_EVENT.PURCHASE_PACKAGE_ONE_ON_ONE,
  },
  {
    id: null,
    title: n_pricing.package.ai,
    subTitle: n_pricing.package.ai_sub,
    type: productTypeEnum.AI_TUTOR,
    isRecommended: false,
    showInB2B: true,
    query: 'ai-tutor',
    eventName: GTM_CLICK_EVENT.PURCHASE_PACKAGE_AI_TUTOR,
  },
]

const ProductTypeSelector = ({ isB2B = false }) => {
  const router = useAppRouter()
  const isMobile = useIsMobile()
  const isInApp = useIsInApp()
  const isWebview = useIsWebview()
  const { t } = useTranslation()
  const getFullUrl = useReturnFullUrl()
  const { sendClickEvent } = useEventTracking()

  const type = router.query.type as string

  const { productType, setProductType, hasAiTutorMembership } = useProductContext()
  const currentTab = productType.type
  const currentTabData = productTypeData.find((type) => type.type === currentTab)

  const navigateToB2BProductPage = () => {
    sendClickEvent({ button_name: GTM_CLICK_EVENT.PURCHASE_MOVE_B2B2C })

    const isInPortal = router.asPath.includes('portal')
    const url = isInPortal ? urls.student.newPortal.pricing.business : urls.student.landing.business

    if (isWebview && !isInApp) {
      router.push(urls.webview.pricing.business, {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: { url: getFullUrl(urls.webview.pricing.business) },
      })
    } else {
      router.push(url)
    }
  }

  const handleMobileProductTypeClick = (type) => {
    sendClickEvent({ button_name: type.eventName })

    const isAITutorTab = type.type === productTypeEnum.AI_TUTOR

    if (isAITutorTab && isWebview && !isInApp) {
      router.push(urls.webview.aiTutor.purchase, {
        appScreenName: APP_SCREEN_NAME.IAP_SCREEN,
        appSendData: { url: getFullUrl(urls.webview.aiTutor.purchase) },
      })
    } else {
      setProductType({ id: type.id, type: type.type })
    }
  }

  const handleWebProductTypeClick = (type) => {
    sendClickEvent({ button_name: type.eventName })
    setProductType({ id: type.id, type: type.type })
  }

  useEffect(() => {
    if (type) {
      productTypeData.find((data) => {
        if (data.query === type) {
          setProductType({ type: data.type, id: data.id })
        }
      })
    }
  }, [type])

  useEffect(() => {
    if (isB2B) {
      setProductType({ id: 19, type: productTypeEnum.PERSONAL_LESSON })
    }
  }, [])

  return (
    <>
      {isMobile ? (
        <div className="flex flex-col gap-y-64">
          <section>
            {isB2B || (
              <div className="px-16 py-32 flex justify-end bg-gray-50 border-t border-gray-100">
                <button className="flex gap-x-2 items-center" onClick={() => navigateToB2BProductPage()}>
                  <span className="Body2-14Bd text-gray-500">{t(n_pricing.button.go_to_b2b2c)}</span>
                  <QIcon_chevron_bold_right width={16} height={16} color={COLORS.GRAY500} />
                </button>
              </div>
            )}
            <div className="sticky top-0 left-0 w-full flex justify-between bg-white" id={productTypeSelectorId}>
              {productTypeData.map((type) => {
                if (isB2B && !type.showInB2B) return null
                if (hasAiTutorMembership && type.type === productTypeEnum.AI_TUTOR) return null
                return (
                  <button
                    key={type.type}
                    className={`py-12 flex-1 text-gray-500 ${
                      productType.type === type.type
                        ? 'text-purple-500 Body1-16Bd border-t-purple-500 border-t-2 border-x'
                        : 'border-b Body1-16Md'
                    }`}
                    onClick={() => handleMobileProductTypeClick(type)}
                  >
                    {t(type.title)}
                  </button>
                )
              })}
            </div>
          </section>
          <section className="flex flex-col items-center">
            <h1 className="Headline1-28Bd text-black">{t(currentTabData.title)}</h1>
            <h2 className="Body1-16Md text-gray-600 text-center">{t(currentTabData.subTitle)}</h2>
          </section>
        </div>
      ) : (
        <section className="flex flex-col gap-y-32">
          {isB2B || (
            <div className="flex justify-end pt-16">
              <button className="flex gap-x-2 items-center" onClick={() => navigateToB2BProductPage()}>
                <span className="Body1-16Bd text-gray-500">{t(n_pricing.button.go_to_b2b2c)}</span>
                <QIcon_chevron_bold_right width={16} height={16} color={COLORS.GRAY500} />
              </button>
            </div>
          )}
          <div className="flex gap-x-20 w-full">
            {isB2B || (
              <div className="relative w-1/2">
                <ProductTypeCard
                  title={t(n_pricing.package.ringle)}
                  subTitle={t(n_pricing.package.ringle_sub)}
                  isSelected={productType.type === productTypeEnum.RINGLE_PACKAGE}
                  onClick={() => handleWebProductTypeClick({ id: 24, type: productTypeEnum.RINGLE_PACKAGE })}
                  className="w-full"
                />
                <BenefitBadge
                  title={t(n_common.btn_recommend)}
                  className="absolute right-8 top-8 bg-white text-red-500 border-red-500"
                />
              </div>
            )}
            <div className="flex gap-x-20 w-1/2">
              {productTypeData.slice(1).map((type) => (
                <ProductTypeCard
                  key={type.type + 1}
                  title={t(type.title)}
                  subTitle={t(type.subTitle)}
                  isSelected={productType.type === type.type}
                  onClick={() => handleWebProductTypeClick({ id: type.id, type: type.type })}
                  className="w-full"
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}

export default ProductTypeSelector
